<template>
  <div class="container-full">
    <Notifications/>
    <StatusMessage/>
    <Navbar :urlDev="urlDev"/>

    <div class="home" :class="{ sidebar: !focused }" v-if="id_site || id_link" id="home">
      <BlobEditor v-if="hintBlob" :blob="hintBlob" :errors="parserErrors" :link_filter_id="link_filter_id"/>
      <ArticleSidebar :loading="loading" v-if="!focused"/>

      <ArticlePreview :article="selectedArticle" :urlDev="urlDev"/>

      <Editor :hints="hints" :loading="loading" :error="hintError" :blob="hintBlob" :urlDev="urlDev"/>
    </div>
    <div v-else class="box-container">
      <form class="form box" @submit.prevent="proceed">
        <div class="form-group">
          <label for="id-site">id_site</label>
          <input type="text" class="form-control" id="id-site" v-model="idSite"/>
        </div>
        <div class="or-line"><span>Or</span></div>
        <div class="form-group">
          <label for="id-link">id_link</label>
          <input type="text" class="form-control" id="id-link" v-model="idLink"/>
        </div>
        <div class="form-group">
          <input type="submit" value="Go" class="btn btn-primary proceed-btn"/>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import Navbar from '../components/Navbar'
import Editor from '../components/Editor'
import BlobEditor from '../components/BlobEditor'
import ArticlePreview from '../components/ArticlePreview'
import ArticleSidebar from '../components/ArticleSidebar'
import Notifications from '../components/Notifications'

import { RepositoryFactory } from '../data/RepositoryFactory'
import { isSuccessful } from '../data/OHMRepository'
import { debug } from '../util/log'
import { Events } from '../events'
import StatusMessage from '../components/StatusMessage'

const OHMRepository = RepositoryFactory.get('ohm')

export default {
  name: 'Home',
  components: { StatusMessage, Navbar, Editor, BlobEditor, ArticlePreview, ArticleSidebar, Notifications },
  data: () => ({
    hints: null,
    loading: false,
    hintError: null,
    hintBlob: null,
    link_filter_id: null,
    parserErrors: [],
    urlDev: '',

    // id_site/id_link input
    idSite: null,
    idLink: null,
  }),
  mounted() {
    this.urlDev = (window.location.host.includes('ohm.dev') ? '.dev' : '') + '.opoint.com'
  },
  created() {
    this.$store.dispatch('hint/getHintFunctions')
  },
  computed: {
    ...mapState({
      id_site: state => state.id_site,
      id_link: state => state.id_link,
    }),
    ...mapState('article', {
      selectedArticle: state => state.selected,
    }),
    ...mapState('hint', {
      hintFunctions: state => state.hintFunctions,
    }),
    ...mapState('site', {
      site: state => state.data,
    }),
    ...mapState('editor', {
      focused: state => state.focused,
    }),
  },
  methods: {
    proceed() {
      if (this.idSite) {
        this.$store.commit('setIdSite', this.idSite)
      } else if (this.idLink) {
        this.$store.commit('setIdLink', this.idLink)
      }
    },
    getHints() {
      return new Promise((resolve, reject) => {
        OHMRepository.getHints(this.id_site, this.id_link)
          .then(response => {
            if (response && response.data && response.data.hints) {
              let hints = response.data.hints

              hints.forEach(hint => {
                hint.statements.forEach(statement => {

                  // TODO: extract the below logic

                  for (let hfi = 0; hfi < Object.keys(this.hintFunctions).length; hfi++) {
                    let hf = this.hintFunctions[Object.keys(this.hintFunctions)[hfi]]
                    if (hf.id === statement.function_id) {
                      // First add empty args if any are missing from statement
                      if (!statement.args || !hf.args) {
                        continue
                      }
                      if (statement.args.length < hf.args.length) {
                        for (let i = 0; i < (hf.args.length - statement.args.length); i++) {
                          statement.args.push({ arg: '' })
                        }
                      }

                      // Then convert any 'list' type arguments into a single comma-separated arg
                      for (let i = 0; i < hf.args.length; i++) {
                        if (hf.args[i].type === 'list') {
                          let merged = ''
                          for (let j = i; j < statement.args.length; j++) {
                            merged += statement.args[j].arg
                            if (j !== statement.args.length - 1) {
                              merged += ','
                            }
                          }
                          statement.args[i].arg = merged

                          // Remove trailing args that are a part of the list
                          // Faster to set new array length than using splice to remove all elements after certain index
                          statement.args.length = i + 1
                          // Break because there shouldn't be anything after the list argument
                          break
                        }
                      }
                      break
                    }
                  }
                })
              })

              this.hints = hints
              this.hintError = null
              resolve(response)
            } else {
              this.hintError = 'An error occurred while loading hints'

              if (response.data.message) {
                this.hintError = response.data.message
              }

              Events.$emit('addNotification', {
                type: 'error',
                msg: this.hintError,
                timeout: 5
              })
              reject(response)
            }
          }).catch(error => {
          debug("home get hints failed")
          if (error && error.response && error.response.data) {
            // if parser_error, show hint blob editor
            // TODO: support fixing several hints at once
            if (error.response.data.parser_error) {
              this.link_filter_id = error.response.data.link_filter_id
              this.hintBlob = error.response.data.hint
              this.parserErrors = error.response.data.errors
            }

            if (error.response.data.message) {
              this.hintError = error.response.data.message
            }
            reject(error)
          } else {
            this.hintError = 'Failed to load hints'
            reject(error)
          }
        })
      })
    },
    updateQuery(id_site, id_link) {
      const q = {}
      if (id_site) {
        this.$store.commit('setIdSite', id_site)
        q['id_site'] = id_site
      }
      if (id_link) {
        this.$store.commit('setIdLink', id_link)
        q['id_link'] = id_link
      }
      this.$router.push({ query: q })

      // Don't attempt to fetch site data before we've even input id_site/id_link
      if (Object.keys(q).length) {
        this.loading = true
        // First load site data, then load everything else in parallel
        OHMRepository.getSite(id_site, id_link)
          .then((response) => {
            if (isSuccessful(response)) {
              this.$store.commit('site/setSiteData', response.data.site)

              Promise.all([this.getHints(), this.$store.dispatch('article/getLatest', id_link)])
                .finally(() => {
                  this.loading = false
                })
            } else {
              debug('Failed to load (1)')
              Events.$emit('addNotification', {
                type: 'error',
                msg: 'Failed to load site data. Make sure the id_site/id_link is correct',
                timeout: 10
              })
            }
          }).catch((error) => {
          if (error.response) {
            debug('Failed to load (2)')
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'Failed to load site data. Make sure the id_site/id_link is correct',
              timeout: 10
            })
          } else {
            debug('Failed to load (3)')
            Events.$emit('addNotification', {
              type: 'error',
              msg: 'Failed to load site data.',
              timeout: 10
            })
          }
          this.loading = false
        })
      }
    }
  },
  watch: {
    '$route.query': {
      deep: true,
      immediate: true,
      handler(val) {
        this.updateQuery(val.id_site, val.id_link)
      }
    },
    id_site(val) {
      if (val) {
        this.updateQuery(val, this.id_link)
      }
    },
    id_link(val) {
      if (val) {
        this.updateQuery(this.id_site, val)
      }
    }
  }
}
</script>

<style lang="sass">
@use '../styles/variables' as v
@import '../vendor/bootstrap/scss/_functions.scss'
@import '../vendor/bootstrap/scss/_variables.scss'

.proceed-btn
  display: block
  margin-top: 5px
  margin-left: auto

.home
  height: 100%
  display: grid
  justify-items: stretch
  overflow: hidden
  grid-template-rows: minmax(0, 1fr) auto
  grid-template-areas: "article" "editor"

  &.sidebar
    grid-template-columns: minmax(0, auto) 1fr
    grid-template-areas: "sidebar article" "editor editor"

.or-line
  border-top: 1px solid v.$dark-gray
  position: relative
  text-align: center
  width: 70%
  margin: 35px auto 0

  span
    text-transform: uppercase
    font-size: v.$font-size-small
    top: -13px
    position: relative
    background: v.$light-black1
    padding: 0 4px

</style>
